import { Component } from '@theme/component';

/**
 * PDP similar items: optional recommendations fetch + inline expand (4 → up to 12).
 */
class ProductSimilarItems extends Component {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (mutation.attributeName === 'data-error') continue;
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      ) {
        continue;
      }
      this.#loadRecommendations();
      break;
    }
  });

  #cachedResponses = {};
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#bindExpand();
    if (this.dataset.fetch === 'true') {
      this.#intersectionObserver.observe(this);
      this.#mutationObserver.observe(this, { attributes: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  #bindExpand() {
    const root = this.querySelector('[data-product-similar-items-root]');
    if (!root) return;

    const btn = root.querySelector('[data-similar-expand]');
    if (!(btn instanceof HTMLButtonElement)) return;

    if (btn.dataset.bound === 'true') return;
    btn.dataset.bound = 'true';

    btn.addEventListener('click', () => {
      root.classList.add('wf-similar-items--expanded');
      btn.setAttribute('aria-expanded', 'true');
      root.querySelectorAll('.wf-similar-items__item[hidden]').forEach((item) => {
        item.removeAttribute('hidden');
      });
      btn.closest('.wf-similar-items__footer')?.remove();
    });
  }

  #loadRecommendations() {
    if (this.dataset.fetch !== 'true') return;

    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) return;
    if (recommendationsPerformed === 'true') return;

    this.#fetchSection(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const fetched = html.querySelector(`product-similar-items[id="${id}"]`);

        if (fetched?.innerHTML?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = fetched.innerHTML;
          this.#bindExpand();
        } else {
          this.#handleError(new Error('No similar items available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  async #fetchSection(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent || 'related'}`;

    const cached = this.#cachedResponses[url];
    if (cached) {
      return { success: true, data: cached };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }
      const text = await response.text();
      this.#cachedResponses[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  #handleError(error) {
    console.error('Product similar items error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading similar items';
  }
}

if (!customElements.get('product-similar-items')) {
  customElements.define('product-similar-items', ProductSimilarItems);
}
